import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddScannerMutation as AddScanner } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddScannerMutation from './AddScannerMutation';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

const addScannerSchema = yup.object().shape({
  title: yup.string().ensure().required(),
});

interface Event {
  id: string;
}

export interface AddScannerFormProps {
  event: Event;
  onSuccess?: (scanner: AddScanner['addScanner']) => void;
}

const AddScannerForm = ({ event, onSuccess }: AddScannerFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
    },
  });

  const [addScanner, { loading, error }] = useMutation(
    AddScannerMutation,
    {
      variables: {
        input: {
          event: { id: event.id },
          info: form.values,
        },
      },
      onCompleted: ({ addScanner: scanner }) => {
        onSuccess?.(scanner);
        notifier.success(t('notifications.scanner_added'));
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addScannerSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addScanner()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_scanner')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('scanner_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="ScannerTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            placeholder={t('scanner_title_placeholder')}
            autoFocus
            id="ScannerTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_scanner')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddScannerForm;
